import { Wrapper as SpinnerWrapper } from "../spinner/spinner.styles";
import { createShouldForwardPropBlockList, deepMerge } from "@components/common/utils/helper";
import { type OptionProps } from "./option";
import {
    getBackgroundColor,
    getBorderRadius,
    getColor,
    getAdornmentsSvgColor,
    getPadding,
    getSpinnerBorderColor,
    getDividerBackground,
} from "./option.styles.utils";
import { getOptionFontSize } from "../form/input-text/input-text.styles.utils";
import styled from "styled-components";

type WrapperProps = Pick<OptionProps, "color" | "disabled" | "active" | "rounded" | "divider" | "dividerType" | "dividerInset">;
type BodyProps = Pick<OptionProps, "size">;
type AdornmentsProps = Pick<OptionProps, "color">;

export const Wrapper = styled("div").withConfig({
    shouldForwardProp: createShouldForwardPropBlockList(["color", "active", "divider", "dividerType", "dividerInset", "rounded"]),
})<WrapperProps>(({ color, disabled, active, divider, dividerType, dividerInset, rounded, theme }) => {
    return deepMerge(
        divider
            ? {
                  height: theme.spacing.px,
                  marginTop: theme.spacing[2],
                  marginBottom: theme.spacing[2],
                  ...getDividerBackground(dividerType, theme),
                  marginInline: dividerInset,
              }
            : {
                  position: "relative",
                  whiteSpace: "nowrap",
                  display: "block",
                  width: "100%",
                  cursor: "pointer",
                  transitionProperty: "background-color, border-color, color",
                  transitionDuration: "300ms",
                  transitionTimingFunction: theme.transitionTimingFunction["out-cubic"],
                  ...(disabled && {
                      opacity: 0.5,
                      pointerEvents: "none",
                  }),
                  ...getColor(color, theme),
                  ...getBorderRadius(rounded, theme),
                  ...getBackgroundColor(active, theme),
                  "&:hover, &:focus": {
                      ...getBackgroundColor(true, theme),
                  },
                  [`${SpinnerWrapper}`]: {
                      ...getSpinnerBorderColor(color, theme),
                  },
              },
    );
});

export const Body = styled("div").withConfig({
    shouldForwardProp: createShouldForwardPropBlockList(["size"]),
})<BodyProps>(({ size, theme }) => ({
    lineHeight: "22px",
    position: "relative",
    display: "flex",
    gap: theme.spacing[2.5],
    alignItems: "center",
    ...deepMerge(getOptionFontSize(size, theme), getPadding(size, theme)),
}));

export const Label = styled("div")(({ theme }) => ({
    whiteSpace: "break-spaces",
    fontWeight: theme.fontWeight.medium,
    textAlign: "left",
}));

export const Loader = styled("div")(() => ({}));

export const Adornments = styled("div").withConfig({
    shouldForwardProp: createShouldForwardPropBlockList(["color"]),
})<AdornmentsProps>(({ color, theme }) => ({
    position: "relative",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: theme.spacing[4],
    height: theme.spacing[4],
    "&:last-child": {
        marginLeft: "auto",
    },
    "& svg": {
        width: theme.spacing[4],
        height: theme.spacing[4],
        ...deepMerge(getAdornmentsSvgColor(color, theme)),
    },
}));
